import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box} from '@mui/material'
import moment from 'moment'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import ChartCard from 'src/components-new/data/ChartCard'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {ALL_QUESTIONNAIRE_FILTERS, buildColumns, DEFAULT_COLUMNS, QUESTIONNAIRE_STATE_LABELS, QuestionnaireRow} from './questionnairesColumns'
import QuestionnaireDialogNew from './QuestionnaireDialogNew'

interface QuestionnaireGraphData {
  moriskyScores: any[]
  compilationDiaryScores: any[]
  satisfactionScores: any[]
}

function QuestionnaireChart({title, data}: {title: string; data: any[]}): JSX.Element {
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Card title={title}>
        <EmptyState title="Nessun dato disponibile" description="Non ci sono statistiche per questo grafico." />
      </Card>
    )
  }

  const xAxisLabels = [3, 2, 1, 0].map(n => moment().subtract(n, 'months').format('MMM YYYY'))

  const options: Highcharts.Options = {
    chart: {type: 'column'},
    title: {text: undefined},
    legend: {enabled: false},
    xAxis: {categories: xAxisLabels},
    yAxis: {min: 0, title: {text: undefined}},
    colors: ['#4472C4'],
    plotOptions: {
      series: {animation: false},
      column: {
        dataLabels: {
          enabled: true,
          style: {color: 'black', textOutline: 'none', fontSize: '12px', fontWeight: '100'},
        },
      },
    },
    credits: {enabled: false},
    series: [{type: 'column', data}],
  }

  return <ChartCard title={title} options={options} />
}

/**
 * New-style equivalent of src/pages/surveys/Surveys.tsx. No page title/breadcrumb is
 * rendered here — HeaderBar derives it from the "survey" nav item label. Pagination
 * stays server-side, matching the legacy page's behavior and apiClient.getSurveys'
 * pagination contract. Structurally the top-level twin of the per-patient
 * SurveysTab.tsx, reading the non patient-scoped `surveysConfiguration` PSP config
 * and apiClient endpoints instead.
 */
export default function QuestionnairesListNew(): JSX.Element {
  const graphs: boolean = useAppSelector(state => state.pspConfigReducer.data.surveysConfiguration?.graphs)
  const columnOptions = useAppSelector(state => state.pspConfigReducer.data.surveysConfiguration?.columnOptions)
  const columnConfig = columnOptions?.length > 0 ? columnOptions : DEFAULT_COLUMNS

  const [graphData, setGraphData] = useState<QuestionnaireGraphData>({
    moriskyScores: [],
    compilationDiaryScores: [],
    satisfactionScores: [],
  })
  const [graphsStatus, setGraphsStatus] = useState<string>(STATUS.PENDING)

  const [rows, setRows] = useState<QuestionnaireRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])

  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<QuestionnaireRow>()
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (!graphs) return
    apiClient.getSurveyGraphData(setGraphsStatus)
      .then((data: any) => { if (data) setGraphData(data) })
      .catch(() => setGraphsStatus(STATUS.REJECTED))
  }, [graphs])

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getSurveys(activeFilters, setStatus, pagination),
      apiClient.getSurveysCount(activeFilters, setStatusCount),
    ])
      .then(([surveys, countResult]) => {
        if (Array.isArray(surveys)) {
          setRows(surveys.map((survey: any) => ({
            ...survey,
            translatedState: QUESTIONNAIRE_STATE_LABELS[survey.state] ?? survey.state,
          })))
        }
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [activeFilters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleRowClick = (row: QuestionnaireRow) => {
    setSelectedQuestionnaire(row)
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    fetchData()
  }

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setActiveFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const columns = buildColumns(columnConfig)

  // Only offer a filter if its column is actually shown — same rule as SurveysTab.tsx.
  const filterConfig: FilterItem[] = useMemo(() => {
    const visibleColumnIds = new Set(columnConfig.map((col: {id: string}) => col.id))
    return ALL_QUESTIONNAIRE_FILTERS
      .filter(({columnId}) => visibleColumnIds.has(columnId))
      .map(({columnId, ...filterItem}) => filterItem)
  }, [columnConfig])

  return (
    <Box>
      {graphs && (
        <Box sx={{display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3}}>
          {graphsStatus === STATUS.REJECTED ? (
            <InlineMessage severity="error">
              {'Impossibile caricare i grafici dei questionari.'}
            </InlineMessage>
          ) : (
            <>
              <QuestionnaireChart title="Questionario Morisky" data={graphData.moriskyScores} />
              <QuestionnaireChart title="Questionario di rilevazione soddisfazione per il programma" data={graphData.satisfactionScores} />
              <QuestionnaireChart title="Questionario monitoraggio diario" data={graphData.compilationDiaryScores} />
            </>
          )}
        </Box>
      )}

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i questionari.'}
        </InlineMessage>
      )}

      {filterConfig.length > 0 && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
          <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
        </Box>
      )}

      <DataTable<QuestionnaireRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun questionario trovato"
        onRowClick={handleRowClick}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      <QuestionnaireDialogNew
        open={dialogOpen}
        onClose={handleDialogClose}
        questionnaire={selectedQuestionnaire}
      />
    </Box>
  )
}
